import * as React from "react";

import { cn } from "./utils";

import { LucideProps, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

export interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<LucideProps>;
  color: string;
  hint?: string;
  comparisonText?: string;
  comparisonClassName?: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface ChartCardProps {
  title: string;
  description: string;
  children: React.ComponentType<any>;
  legends?: React.ComponentType<any>;
  controls?: React.ComponentType<any>;
  value?: string | number;
  icon: React.ComponentType<LucideProps>;
  color: string;
  hint?: string;
}
function Card({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card"
      className={cn(
        "bg-card text-card-foreground flex flex-col gap-4 rounded-lg border",
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 pt-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <h4
      data-slot="card-title"
      className={cn("leading-none", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <p
      data-slot="card-description"
      className={cn("text-muted-foreground", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6 [&:last-child]:pb-6", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 pb-6 [.border-t]:pt-6", className)}
      {...props}
    />
  );
}

function StatCard({ title, value, icon: Icon, color, hint, comparisonText, comparisonClassName, onClick, disabled }: StatCardProps) {
  const isClickable = !disabled && !!onClick;
  const tooltipText = disabled ? [hint, 'Not available in Community Edition.'].filter(Boolean).join(' ') : hint;

  return (
    <Card
      className={`rounded-lg shadow-sm border border-border relative ${disabled ? 'opacity-50 grayscale' : ''} ${isClickable ? 'cursor-pointer hover:shadow-md hover:border-primary/50 transition-all' : ''}`}
      onClick={isClickable ? onClick : undefined}
    >
      {tooltipText && (
        <div className="absolute top-2 right-2 z-10">
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-4 h-4 text-muted-foreground cursor-help hover:text-foreground transition-colors" />
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="center"
              className="max-w-[300px] text-xs bg-popover text-foreground border border-border shadow-lg"
              sideOffset={8}
            >
              {tooltipText}
            </TooltipContent>
          </Tooltip>
        </div>
      )}
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
            {comparisonText && (
              <p className={cn("text-xs mt-1", comparisonClassName || 'text-muted-foreground')}>
                {comparisonText}
              </p>
            )}
          </div>
          <div className={`min-w-[3rem] w-12 h-12 max-w-[3rem] max-h-[3rem] rounded-lg flex items-center justify-center bg-${color}-50 dark:bg-${color}-900 bg-opacity-10 flex-shrink-0`}>
            <Icon className={`min-w-[1.5rem] w-6 h-6 max-w-[1.5rem] max-h-[1.5rem] text-${color}-600 dark:text-${color}-400 flex-shrink-0`} />
          </div>

        </div>
      </CardContent>

    </Card>
  );
}

function ChartCard({ title, description, children, icon: Icon, color, legends, controls, value, hint }: ChartCardProps) {
  const ChildrenComponent = children;
  const LegendsComponent = legends;
  const ControlsComponent = controls;

  return (
    <Card className="rounded-lg shadow-sm border border-border relative">
       {hint && (
                <div className="absolute top-2 right-2 z-10">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-muted-foreground cursor-help hover:text-foreground transition-colors" />
                        </TooltipTrigger>
                        <TooltipContent
                            side="top"
                            align="center"
                            className="max-w-[300px] text-xs bg-popover text-foreground border border-border shadow-lg"
                            sideOffset={8}
                        >
                            {hint}
                        </TooltipContent>
                    </Tooltip>
                </div>
            )}
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
          {title}
          {value && <span className="ml-auto">{value}</span>}
        </CardTitle>
        <CardDescription>{description}  {value && <span className="float-end">Total</span>}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4">
          <div className="flex-1">
            {ControlsComponent && (
              <div className="flex flex-1">
                <ControlsComponent />
              </div>
            )}
            <div className="w-full">
              <ChildrenComponent />
            </div>
          </div>
          {LegendsComponent && (
            <div className="flex flex-col justify-center space-y-2 min-w-[120px]">
              <LegendsComponent />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  StatCard,
  ChartCard
};
